import { BlogMarkdownDescription } from "../BlogHomePage";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { getUrls } from "@portal/app/_common/lib/urls";
import type { BlogPostCard } from "@portal/app/_server/services/blogPost/types";
import { BlogPostDate } from "./BlogPostDate";
import { BLOG_CARD_IMAGE_HEIGHT_REM, getBlogCardImageProps } from "../blogImage";

export const BlogPost = ({ blogPost }: { blogPost: BlogPostCard }) => {
  const urls = getUrls();

  return (
    <BlogContainer component="a" href={urls.Blog(blogPost.slug)} data-testid="blog-post-item">
      {blogPost.featuredImage?.url ? (
        <BlogImage
          {...getBlogCardImageProps(blogPost.featuredImage.url, blogPost.featuredImage)}
          alt={blogPost.featuredImage.altText || blogPost.title}
          decoding="async"
        />
      ) : (
        <BlogImagePlaceholder />
      )}
      {blogPost.vocationAdjective && <VocationChip label={blogPost.vocationAdjective} />}
      <BlogContent>
        <BlogTitle variant="new_h3">
          <strong>{blogPost.title}</strong>
        </BlogTitle>
        <BlogMarkdownDescription value={blogPost.excerpt} />
        <BlogFooter variant="disclaimer">
          <BlogPostDate createdAt={blogPost.createdAt} updatedAt={blogPost.updatedAt} />
          {blogPost.authorName !== null && <>&nbsp;&bull;&nbsp;Written by {blogPost.authorName}</>}
        </BlogFooter>
      </BlogContent>
    </BlogContainer>
  );
};

const BlogContainer = styled(Box)<{ component: "a"; href: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
`;

const BlogImage = styled("img")`
  border-radius: 1rem;
  height: ${BLOG_CARD_IMAGE_HEIGHT_REM}rem;
  width: 100%;
  object-fit: cover;
  object-position: left center;
  color: transparent;
`;

const BlogImagePlaceholder = styled(Box)`
  border-radius: 1rem;
  height: ${BLOG_CARD_IMAGE_HEIGHT_REM}rem;
  width: 100%;
  background: ${({ theme }) => theme.palette.dream.grayLight};
`;

// Fixed rhythm so every card in a row stacks title/excerpt/footer at the same
// y position; any leftover height from row stretching collects below the footer.
const BlogContent = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 0.75rem;
  padding-top: 1rem;
`;

const BlogTitle = styled(Typography)`
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.645rem; // 2 lines of new_h3 (1.15rem x 1.15); fallback for browsers without lh
  min-height: 2lh;
`;

const BlogFooter = styled(Typography)`
  margin-top: auto;
  color: ${({ theme }) => theme.palette.dream.grayMediumDark};
`;

const VocationChip = styled(Chip)`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background-color: #dbffd8;
`;
